import {Platform} from 'react-native';
import * as FileSystem from 'expo-file-system';
import {ApiConfig} from '../config/ApiConfig';
import {getAccessToken} from '../helpers/storage';
import {createUnsyncedRecordError, isLocalOnlyRecordId} from '../helpers/serviceRecordId';
import {discardPreparedPhoto, preparePhotoForUpload} from '../helpers/preparePhotoForUpload';

/**
 * Work-order photo gallery API (Szerviz / RepairPhotoScreen).
 *
 * Photos are NOT a field on the work order: they are stored as Service Module
 * documents linked to the work order, which is what the backend actually
 * persists and what the admin's DocumentsCard already renders. Before this
 * module the screen only pushed a device-local `file://` URI into
 * `worksheet.photos` and called `saveWorksheet()`, whose PATCH payload
 * (`buildWorkOrderPatchPayload`) has no `photos` key — so nothing ever reached
 * the server and the next list refresh wiped the picture.
 *
 * Backend (HappyPOS-backend src/api/controllers/serviceModule.controller.js):
 *   - POST   /api/serviceModule/documents/upload   multipart, field `file`
 *   - GET    /api/serviceModule/documents          ?linkedEntityId&linkedEntityType&limit
 *   - DELETE /api/serviceModule/documents/{id}     soft-delete
 *
 * The category lives in the document's `tag` as `photo:<category>` (the upload
 * endpoint accepts exactly one tag), which also marks the document as a gallery
 * photo rather than an ordinary attachment.
 */

const DOCUMENTS_URL = `${ApiConfig.apiURL}/api/serviceModule/documents`;
const UPLOAD_URL = `${DOCUMENTS_URL}/upload`;

/** `linkedEntityType` the backend stores work-order documents under. */
export const WORK_ORDER_ENTITY_TYPE = 'WorkOrder';

/** Tag prefix that marks a Service Module document as a work-order photo. */
export const PHOTO_TAG_PREFIX = 'photo:';

/** Category used when a document carries no `photo:<category>` tag. */
export const DEFAULT_PHOTO_CATEGORY = 'other';

/**
 * Documents pulled per request. The endpoint paginates at 20 by default, which
 * silently truncated a gallery; the backend accepts an explicit `limit`.
 * @type {number}
 */
const PHOTO_PAGE_LIMIT = 200;

const IMAGE_EXTENSION_RE = /\.(jpe?g|png|gif|webp|heic|heif|bmp|tiff?)$/i;

/**
 * Bearer + optional store scope. The app has no restaurant switcher, so the
 * backend would otherwise fall back to the technician's FIRST store; sending the
 * work order's own store keeps upload, list and delete on one scope.
 *
 * @param {string} [restaurantId] Work order's restaurant id, when known.
 * @returns {Promise<Object>} Header map (never includes Content-Type).
 */
const authHeaders = async (restaurantId) => {
    const accessToken = await getAccessToken();

    if (!accessToken) {
        const authError = new Error('Authentication required');

        authError.code = 'AUTH_REQUIRED';
        throw authError;
    }

    return {
        Authorization: `Bearer ${accessToken}`,
        Accept: 'application/json',
        ...(restaurantId ? {'x-restaurant-id': String(restaurantId)} : {}),
    };
};

/**
 * Turn a non-2xx response into an Error carrying the server's own message, so
 * the screen can show the real reason (403 disabled feature, 413 too large, …)
 * instead of a generic failure.
 *
 * @param {Response} response
 * @returns {Promise<Error>}
 */
const buildErrorFromResponse = async (response) => {
    try {
        const body = await response.json();
        const message = body?.message || body?.error || body?.data?.message || response.statusText;
        const error = new Error(message || `Request failed with status ${response.status}`);

        error.status = response.status;
        if (body?.code) {
            error.code = body.code;
        }

        return error;
    } catch (_error) {
        const error = new Error(response.statusText || `Request failed with status ${response.status}`);

        error.status = response.status;

        return error;
    }
};

/**
 * @param {Response} response
 * @returns {Promise<*>} Parsed JSON, or `true` for an empty body.
 */
const parseApiResponse = async (response) => {
    if (!response.ok) {
        throw await buildErrorFromResponse(response);
    }
    if (response.status === 204) {
        return true;
    }

    const text = await response.text();

    if (!text) {
        return true;
    }

    try {
        return JSON.parse(text);
    } catch (_error) {
        return true;
    }
};

/**
 * `photo:damage` → `damage`. Any other tag (or none) falls back to `other`.
 *
 * @param {string[]} tags
 * @returns {string}
 */
const categoryFromTags = (tags) => {
    const list = Array.isArray(tags) ? tags : [];
    const photoTag = list.find(tag => typeof tag === 'string' && tag.startsWith(PHOTO_TAG_PREFIX));

    return photoTag ? photoTag.slice(PHOTO_TAG_PREFIX.length) || DEFAULT_PHOTO_CATEGORY : DEFAULT_PHOTO_CATEGORY;
};

/**
 * A work order can also carry PDFs and spreadsheets uploaded from the admin —
 * the gallery must only show pictures. A document counts as a photo when it was
 * tagged as one, or when it is plainly an image by MIME/extension (covers files
 * attached before this flow existed).
 *
 * @param {Object} doc Raw ServiceModuleDocument.
 * @returns {boolean}
 */
const isPhotoDocument = (doc) => {
    const tags = Array.isArray(doc?.tags) ? doc.tags : [];

    if (tags.some(tag => typeof tag === 'string' && tag.startsWith(PHOTO_TAG_PREFIX))) {
        return true;
    }
    if (typeof doc?.mimeType === 'string' && doc.mimeType.startsWith('image/')) {
        return true;
    }

    return IMAGE_EXTENSION_RE.test(String(doc?.name || doc?.path || ''));
};

/**
 * Map a ServiceModuleDocument onto the shape the gallery renders.
 *
 * @param {Object} doc Raw document from the backend.
 * @returns {{_id: string, uri: string, category: string, name: string,
 *   mimeType: string, size: number, createdAt: (string|null), source: string}}
 */
export const normalizePhotoDocument = (doc = {}) => ({
    _id: String(doc._id || doc.id || ''),
    uri: doc.path || doc.url || '',
    category: categoryFromTags(doc.tags),
    name: doc.name || '',
    mimeType: doc.mimeType || '',
    size: Number(doc.size || 0) || 0,
    createdAt: doc.createdAt || doc.updatedAt || null,
    source: doc.source || 'serviceModule',
});

/**
 * Load every photo attached to a work order, newest first.
 *
 * @param {string} workOrderId Mongo `_id` of the work order.
 * @param {{restaurantId?: string, entityType?: string}} [options] Work order's store,
 *   when known; `entityType` overrides the linked entity type (default `WorkOrder`,
 *   e.g. `utility_reading` for the utility records module).
 * @returns {Promise<Array<Object>>} Normalized photos (empty when there are none).
 */
export async function fetchWorkOrderPhotos(workOrderId, options = {}) {
    if (!workOrderId) {
        return [];
    }

    // A work sheet created offline has no server document, so `linkedEntityId`
    // would be a local-only id the API cannot cast — there is nothing to list.
    if (isLocalOnlyRecordId(workOrderId)) {
        return [];
    }

    // Hand-built, NOT URLSearchParams: React Native ships a stub whose
    // `toString()` throws "not implemented", so the query would die on device
    // while working fine on web.
    const query = [
        `linkedEntityId=${encodeURIComponent(String(workOrderId))}`,
        `linkedEntityType=${encodeURIComponent(options.entityType || WORK_ORDER_ENTITY_TYPE)}`,
        `limit=${PHOTO_PAGE_LIMIT}`,
        'page=1',
    ].join('&');

    const response = await fetch(`${DOCUMENTS_URL}?${query}`, {
        method: 'GET',
        headers: await authHeaders(options.restaurantId),
    });

    const body = await parseApiResponse(response);
    const docs = Array.isArray(body) ? body : (body?.docs || body?.data?.docs || []);

    return docs
        .filter(isPhotoDocument)
        .map(normalizePhotoDocument)
        .filter(photo => photo._id && photo.uri)
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
}

/**
 * ASCII-safe upload filename. The backend builds its object key as
 * `service/<restaurantId>/<ts>-<name>`, so a localized label ("Sérülés / Hiba")
 * would push the file into an accidental sub-folder and produce a URL full of
 * escapes. The human-readable category travels in the tag instead.
 *
 * @param {string} category Photo category key.
 * @param {string} extension File extension without the dot.
 * @param {number} stamp Millisecond timestamp.
 * @returns {string}
 */
const buildPhotoFileName = (category, extension, stamp) => {
    const safeCategory = String(category || DEFAULT_PHOTO_CATEGORY)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || DEFAULT_PHOTO_CATEGORY;

    return `photo-${safeCategory}-${stamp}.${extension}`;
};

/** MIME types and extensions Apple's container format arrives under. */
const HEIF_EXTENSIONS = ['heic', 'heif'];

/**
 * Resolve the picked asset's file name + MIME type.
 *
 * The format is declared HONESTLY, including HEIC. iOS hands the raw,
 * `quality`-ignoring HEIC original back from the photo library unless the picker
 * is asked for the compatible representation (see `helpers/imagePickerOptions`),
 * and an Android gallery can still produce one; labelling those bytes
 * `image/jpeg` is strictly worse than saying what they are. The upload would
 * succeed, the object would be stored under a MIME only Apple can decode, and
 * the picture would silently never render anywhere else. A server that refuses
 * HEIC answers instead, and `uploadWorkOrderPhoto` retries the upload relabelled
 * as JPEG — which is exactly the behaviour this function used to hardcode.
 *
 * @param {{uri?: string, fileName?: string, mimeType?: string, type?: string}} asset
 *   expo-image-picker asset.
 * @param {string} category Photo category key.
 * @returns {{name: string, type: string}} Upload filename and MIME type.
 */
const describePhotoAsset = (asset, category) => {
    const source = String(asset?.fileName || asset?.uri || '');
    const matchedExtension = /\.(\w+)(?:\?|$)/.exec(source);
    const rawExtension = (matchedExtension?.[1] || '').toLowerCase();
    const declaredMime = String(asset?.mimeType || '').toLowerCase();
    const stamp = Date.now();
    const heifExtension = HEIF_EXTENSIONS.find(
        ext => rawExtension === ext || declaredMime === `image/${ext}`
    );

    if (heifExtension) {
        return {
            name: buildPhotoFileName(category, heifExtension, stamp),
            type: `image/${heifExtension}`,
        };
    }

    const isPng = declaredMime === 'image/png' || rawExtension === 'png';

    return {
        name: buildPhotoFileName(category, isPng ? 'png' : 'jpg', stamp),
        type: isPng ? 'image/png' : 'image/jpeg',
    };
};

/**
 * The same picture, relabelled as JPEG — the pre-HEIC behaviour, kept as the
 * retry for a server whose attachment allowlist has not learned HEIC yet. The
 * timestamp in the name is preserved so both attempts file under one object key.
 *
 * @param {{name: string, type: string}} descriptor Honest descriptor.
 * @returns {{name: string, type: string}} JPEG-labelled descriptor.
 */
const describeAsJpeg = (descriptor) => ({
    name: descriptor.name.replace(/\.(heic|heif)$/i, '.jpg'),
    type: 'image/jpeg',
});

/**
 * Build the multipart body for the picked asset.
 *
 * React Native's fetch accepts the `{uri, name, type}` descriptor, but on web
 * (and the Tauri desktop build) that object is serialized as the literal string
 * "[object Object]" and the server answers "No file uploaded" — there the asset
 * has to be read into a Blob first.
 *
 * @param {{uri: string, file?: File}} asset Picked asset.
 * @param {{name: string, type: string}} descriptor Upload filename and MIME type.
 * @returns {Promise<FormData>}
 */
const buildPhotoFormData = async (asset, {name, type}) => {
    const form = new FormData();

    if (Platform.OS === 'web') {
        // `asset.file` is present for a gallery pick; a camera capture only
        // hands back a blob:/data: URI, which fetch can read back locally.
        const blob = asset?.file || await (await fetch(asset.uri)).blob();

        form.append('file', blob, name);
    } else {
        form.append('file', {uri: asset.uri, name, type});
    }

    return form;
};

/**
 * Peak JS-heap cost the base64 fallback is allowed to incur.
 *
 * That transport materializes the picture THREE times over — the base64 string,
 * the JSON envelope that embeds it verbatim, and the body handed to the network
 * stack — all reachable at once across the `await`. Unbounded, on a 12 MP iPhone
 * original, that is tens of megabytes of transient strings, which is what got
 * the app killed by the iOS memory watchdog mid-upload. 700 KB on disk is
 * ~933 KB of base64: under a megabyte of copies, and also under the API
 * gateway's shared JSON body limit, so a larger file would have been refused
 * with a 413 anyway rather than stored.
 * @type {number}
 */
const BASE64_FALLBACK_MAX_BYTES = 700 * 1024;

/**
 * Statuses that mean the server REFUSED the request rather than failed to carry
 * it. Re-sending the same bytes over another transport cannot change the answer.
 * @type {number[]}
 */
const TERMINAL_UPLOAD_STATUSES = [400, 401, 403, 404, 413, 415];

/**
 * Statuses the attachment allowlist answers with: 415 from the base64 decoder,
 * 400 from multer's `fileFilter` (whose bare Error carries no status, so the
 * controller defaults it). Either one means "not this MIME type".
 * @type {number[]}
 */
const TYPE_REFUSAL_STATUSES = [400, 415];

/**
 * `FileSystem.uploadAsync` resolves with a plain result object for EVERY status
 * — it does not throw on 4xx/5xx — so wrap it in the minimal `Response` surface
 * `parseApiResponse`/`buildErrorFromResponse` already speak. One error contract
 * (`error.status`, `error.code`, the server's own message) then reaches the
 * screen no matter which transport ran.
 *
 * @param {{status: number, body?: string}} result Upload result.
 * @returns {Object} Response-like object.
 */
const toResponseLike = (result) => ({
    ok: result?.status >= 200 && result?.status < 300,
    status: result?.status || 0,
    statusText: '',
    text: async () => result?.body || '',
    json: async () => JSON.parse(result?.body || 'null'),
});

/**
 * Upload one photo and attach it to a work order.
 *
 * TRANSPORT, and why there are three of them.
 *
 * 1. NATIVE STREAM (device, preferred). `FileSystem.uploadAsync` streams the
 *    file straight from disk into a multipart body inside the native networking
 *    stack — the picture never touches the JS heap at all. This is the fix for
 *    the iOS `WatchdogTermination` crashes: the previous primary transport
 *    (base64 JSON, now #3) kept several full-size copies of a multi-megabyte
 *    photo alive simultaneously. It also sidesteps the API gateway's shared JSON
 *    body limit, which a real photo exceeds, so uploads that were quietly 413ing
 *    now go through.
 * 2. BASE64 JSON (device fallback, size-guarded). The API sits behind a
 *    Cloudflare proxy chain that can turn a `multipart/form-data` POST into an
 *    opaque `502 origin_bad_gateway` regardless of body size — the failure
 *    tracks the CONTENT TYPE, not the bytes, and a technician on a mobile
 *    network hits it far more often than a browser on the office LAN. That is
 *    why this transport exists and why it is kept: when the stream upload fails
 *    to be CARRIED (5xx, transport error), the JSON envelope still gets through.
 *    It is skipped for a file large enough to re-create the memory problem.
 * 3. BROWSER MULTIPART (web/Tauri, unchanged). The transport the admin panel has
 *    always used; `uploadAsync` has no web implementation.
 *
 * A refusal the server MEANS (feature disabled, too large, wrong type) is
 * surfaced immediately — with one exception: a HEIC the allowlist rejects is
 * retried once relabelled as JPEG, so an older server behaves exactly as before.
 *
 * Whatever was picked is FIRST shrunk to an upload size every device can carry
 * (`helpers/preparePhotoForUpload`). Without that step Android produced bodies
 * several times larger than iOS at the same `quality` — its picker re-encodes at
 * full sensor resolution — which both made transport #1 fail far more often and
 * pushed the picture past the size gate on transport #2, leaving Android with no
 * working path at all while the same code uploaded fine from an iPhone.
 *
 * @param {string} workOrderId Mongo `_id` of the work order.
 * @param {{uri: string, fileName?: string, mimeType?: string, file?: File}} asset
 *   expo-image-picker asset.
 * @param {string} category Photo category key (see `PHOTO_CATEGORIES`).
 * @param {{restaurantId?: string, entityType?: string}} [options] Work order's store,
 *   when known; `entityType` overrides the linked entity type (default `WorkOrder`).
 * @returns {Promise<Object>} The normalized, stored photo.
 */
export async function uploadWorkOrderPhoto(workOrderId, asset, category, options = {}) {
    if (!workOrderId) {
        throw new Error('Missing work order id');
    }
    if (isLocalOnlyRecordId(workOrderId)) {
        throw createUnsyncedRecordError(workOrderId);
    }
    if (!asset?.uri) {
        throw new Error('No photo selected');
    }

    const tag = `${PHOTO_TAG_PREFIX}${category || DEFAULT_PHOTO_CATEGORY}`;
    const headers = await authHeaders(options.restaurantId);
    const fields = {
        linkedEntityType: options.entityType || WORK_ORDER_ENTITY_TYPE,
        linkedEntityId: String(workOrderId),
        tag,
    };
    // Every transport below sends THIS asset, not the picked one: a resized,
    // honestly-typed copy small enough for all three of them. Falls back to the
    // original on a device whose decoder cannot read the source.
    const source = await preparePhotoForUpload(asset);
    // Stamped ONCE, then shared by every attempt. The name carries `Date.now()`
    // and the object key is built from it, so re-deriving it per transport would
    // let a fallback after a partial success fork the picture into two objects.
    const descriptor = describePhotoAsset(source, category);

    /**
     * @param {Response} response Upload response.
     * @returns {Promise<Object>} Normalized photo.
     */
    const toPhoto = async (response) => {
        const body = await parseApiResponse(response);
        const doc = body?._id ? body : (body?.data || body?.document || {});

        return normalizePhotoDocument(doc);
    };

    /**
     * @param {{name: string, type: string}} attempt Descriptor to declare.
     * @returns {Promise<Object>} Photo stored over the browser multipart transport.
     */
    const postMultipart = async (attempt) => {
        const form = await buildPhotoFormData(source, attempt);

        Object.entries(fields).forEach(([key, value]) => form.append(key, value));

        return toPhoto(await fetch(UPLOAD_URL, {
            method: 'POST',
            // No Content-Type: the multipart boundary is generated by fetch itself.
            headers,
            body: form,
        }));
    };

    /**
     * @param {{name: string, type: string}} attempt Descriptor to declare.
     * @returns {Promise<Object>} Photo stored without ever loading it into JS.
     */
    const postNativeStream = async (attempt) => {
        const result = await FileSystem.uploadAsync(UPLOAD_URL, source.uri, {
            httpMethod: 'POST',
            uploadType: FileSystem.FileSystemUploadType.MULTIPART,
            fieldName: 'file',
            mimeType: attempt.type,
            // The multipart part's own filename is the picker's temp name, so
            // the human-readable one travels as a sibling field — the controller
            // prefers `name` over `req.file.originalname`.
            parameters: {...fields, name: attempt.name, fileName: attempt.name},
            headers,
            // NOT the default BACKGROUND session: that one "doesn't fail if the
            // server or your connection is down… it continues retrying until the
            // task succeeds or is canceled", which would leave the save spinner
            // running forever on a dead mobile network.
            sessionType: FileSystem.FileSystemSessionType.FOREGROUND,
        });

        return toPhoto(toResponseLike(result));
    };

    /** @returns {Promise<Object>} Photo stored over the base64 JSON transport. */
    const postBase64Json = async (attempt) => {
        const file = await FileSystem.readAsStringAsync(source.uri, {
            encoding: FileSystem.EncodingType?.Base64 || 'base64',
        });

        if (!file) {
            throw new Error('The selected image is empty');
        }

        return toPhoto(await fetch(UPLOAD_URL, {
            method: 'POST',
            headers: {
                ...headers,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                ...fields,
                file,
                fileName: attempt.name,
                mimeType: attempt.type,
            }),
        }));
    };

    // A HEIC gets one relabelled retry, because a server whose allowlist predates
    // HEIC answers 415/400 for the honest type but stores the very same bytes
    // when they are declared as JPEG — which is what this client used to do
    // unconditionally. Anything else is a single attempt.
    const attempts = HEIF_EXTENSIONS.some(ext => descriptor.type === `image/${ext}`)
        ? [descriptor, describeAsJpeg(descriptor)]
        : [descriptor];
    let lastAttempt = attempts[0];

    /**
     * Push the picture through one transport, relabelling a HEIC the server's
     * allowlist will not take and re-throwing everything else untouched.
     *
     * @param {Function} post Transport, called with a descriptor.
     * @returns {Promise<Object>} The normalized, stored photo.
     */
    const postWithRelabelRetry = async (post) => {
        let lastError = null;

        for (let index = 0; index < attempts.length; index += 1) {
            lastAttempt = attempts[index];
            try {
                return await post(lastAttempt);
            } catch (error) {
                lastError = error;

                const canRelabel = index < attempts.length - 1
                    && TYPE_REFUSAL_STATUSES.includes(error?.status);

                if (!canRelabel) {
                    throw error;
                }
            }
        }

        throw lastError;
    };

    // The rendered copy is a cache file of this call's own making, so it is
    // dropped however the upload ends — success, refusal or transport error.
    try {
        if (Platform.OS === 'web' || typeof FileSystem.uploadAsync !== 'function') {
            return await postWithRelabelRetry(postMultipart);
        }

        try {
            return await postWithRelabelRetry(postNativeStream);
        } catch (streamError) {
            // A refusal the server MEANT fails identically over any transport.
            if (TERMINAL_UPLOAD_STATUSES.includes(streamError?.status)) {
                throw streamError;
            }

            // The stream could not be CARRIED (502 from the proxy chain, 5xx,
            // offline). The JSON envelope survives that — but only pay for it
            // when the copies it makes are small enough to be safe, otherwise
            // the honest transport error beats an out-of-memory kill, or the 413
            // that the shared body limit has waiting anyway. A prepared photo is
            // aimed at exactly this ceiling, so the fallback is reachable on
            // every device rather than only on the ones with small cameras.
            const {size = 0} = await FileSystem.getInfoAsync(source.uri, {size: true}) || {};

            if (size > BASE64_FALLBACK_MAX_BYTES) {
                throw streamError;
            }

            return await postBase64Json(lastAttempt);
        }
    } finally {
        discardPreparedPhoto(source);
    }
}

/**
 * Tag of a non-photo document attached to a work order (PDF, Office file, …).
 * It deliberately lacks the `photo:` prefix, so the photo gallery keeps
 * ignoring it while the server still gates it on the `documents` feature.
 * @type {string}
 */
export const WORK_ORDER_DOCUMENT_TAG = 'document';

/**
 * Upload a non-photo file (PDF, Word, Excel, CSV, TXT) to a work order through
 * the same `POST /api/serviceModule/documents/upload` endpoint the photos use.
 *
 * Unlike {@link uploadWorkOrderPhoto} the bytes are sent as-is (no resize, no
 * HEIC relabel). On device the file is STREAMED from disk with
 * `FileSystem.uploadAsync` (zero JS heap, FOREGROUND session); a `content://`
 * URI the native uploader rejects falls back to a fetch multipart. Web / Tauri
 * always use the browser multipart.
 *
 * @param {string} workOrderId Mongo `_id` of the work order.
 * @param {{uri: string, fileName?: string, name?: string, mimeType?: string, file?: File}} asset Local file.
 * @param {{restaurantId?: string, tag?: string, entityType?: string}} [options] Work order's store,
 *   tag override (default {@link WORK_ORDER_DOCUMENT_TAG}) and linked entity type override.
 * @returns {Promise<Object>} The stored `ServiceModuleDocument`.
 */
export async function uploadWorkOrderDocument(workOrderId, asset, options = {}) {
    if (!workOrderId) {
        throw new Error('Missing work order id');
    }
    if (isLocalOnlyRecordId(workOrderId)) {
        throw createUnsyncedRecordError(workOrderId);
    }
    if (!asset?.uri) {
        throw new Error('No file selected');
    }

    const name = asset.fileName || asset.name || `document-${Date.now()}`;
    const type = asset.mimeType || asset.type || 'application/octet-stream';
    const headers = await authHeaders(options.restaurantId);
    const fields = {
        linkedEntityType: options.entityType || WORK_ORDER_ENTITY_TYPE,
        linkedEntityId: String(workOrderId),
        tag: options.tag || WORK_ORDER_DOCUMENT_TAG,
        name,
    };

    /** @returns {Promise<Object>} Document stored over a fetch multipart. */
    const postMultipart = async () => {
        const form = new FormData();

        if (Platform.OS === 'web') {
            const blob = asset.file || await (await fetch(asset.uri)).blob();

            form.append('file', blob, name);
        } else {
            form.append('file', {uri: asset.uri, name, type});
        }
        Object.entries(fields).forEach(([key, value]) => form.append(key, value));

        // No Content-Type: the multipart boundary is generated by fetch itself.
        const body = await parseApiResponse(await fetch(UPLOAD_URL, {method: 'POST', headers, body: form}));

        return body?._id ? body : (body?.data || body?.document || body);
    };

    if (Platform.OS === 'web' || typeof FileSystem.uploadAsync !== 'function') {
        return postMultipart();
    }

    let result = null;

    try {
        result = await FileSystem.uploadAsync(UPLOAD_URL, asset.uri, {
            httpMethod: 'POST',
            uploadType: FileSystem.FileSystemUploadType.MULTIPART,
            fieldName: 'file',
            mimeType: type,
            parameters: fields,
            headers,
            sessionType: FileSystem.FileSystemSessionType.FOREGROUND,
        });
    } catch (_streamError) {
        // Native stream unavailable for this URI (e.g. content://) — fetch can read it.
        return postMultipart();
    }

    const body = await parseApiResponse(toResponseLike(result));

    return body?._id ? body : (body?.data || body?.document || body);
}

/**
 * Soft-delete a work-order photo.
 *
 * @param {string} documentId Document `_id`.
 * @param {{restaurantId?: string}} [options] Work order's store, when known.
 * @returns {Promise<boolean>} `true` once the server confirmed the delete.
 */
export async function deleteWorkOrderPhoto(documentId, options = {}) {
    if (!documentId) {
        throw new Error('Missing document id');
    }

    const response = await fetch(`${DOCUMENTS_URL}/${encodeURIComponent(documentId)}`, {
        method: 'DELETE',
        headers: await authHeaders(options.restaurantId),
    });

    await parseApiResponse(response);

    return true;
}

export default {
    WORK_ORDER_ENTITY_TYPE,
    PHOTO_TAG_PREFIX,
    DEFAULT_PHOTO_CATEGORY,
    normalizePhotoDocument,
    fetchWorkOrderPhotos,
    uploadWorkOrderPhoto,
    deleteWorkOrderPhoto,
};
